import React, { useState, useEffect } from "react";
import {
  FaSync,
  FaGithub,
  FaCodeBranch,
  FaUsers,
  FaExclamationCircle,
  FaGitAlt,
  FaChevronDown,
  FaChevronUp,
  FaCalendarAlt,
  FaUser,
  FaTags,
  FaPlus,
  FaTimes,
  FaCog,
  FaUnlink,
  FaChartLine,
  FaClone,
  FaEye,
  FaArrowUp,
  FaArrowDown,
  FaBug,
} from "react-icons/fa";
import HorizontalBarChart from "../../components/Dashboard/GithubCharts/HorizontalBarChart";
import Table from "../../components/Dashboard/Charts/Table";
import StatCards from "../../components/Dashboard/GithubCharts/StateCards";
import Sidebar from "../../components/Dashboard/Sidebar";
import Header from "../../components/Dashboard/Header";
import Footer from "../../components/Dashboard/Footer";
import LoadingSpinner from "./LoadingSpinner";
import TopTagsChart from "../../components/Dashboard/GithubCharts/TopTagsChart";
import WeeklyTrendAreaChart from "../../components/Dashboard/GithubCharts/WeeklyTrendAreaChart";
import { useWeeklySummary } from "../../hooks/useWeeklySummary";

function GithubStats() {
  // ---------- Layout & shell ----------
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeSidebarItem, setActiveSidebarItem] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [checkingIntegration, setCheckingIntegration] = useState(true);

  const urlParams = new URLSearchParams(window.location.search);
  const projectId =
    urlParams.get("project_id") || localStorage.getItem("selectedProjectId");

  if (!projectId) {
    console.warn("projectId not found in URL or localStorage");
  }

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const { weeklySummary, loadingWeekly, fetchWeeklySummary } =
    useWeeklySummary(BACKEND_URL, projectId);

    useEffect(() => {
          document.title = "DCode | Repository insights";
        }, []);

  // ---------- Data state ----------
  const [syncStatus, setSyncStatus] = useState({});
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [isSyncing, setIsSyncing] = useState({});
  const [isGithubConnected, setIsGithubConnected] = useState(false);
  const [integrationInfo, setIntegrationInfo] = useState(null);
  const [githubError, setGithubError] = useState(null);
  const [connectedRepos, setConnectedRepos] = useState([]); // Connected repos only
  const [availableRepos, setAvailableRepos] = useState([]); // All unconnected repos
  const [showAvailableRepos, setShowAvailableRepos] = useState(false); // Toggle to show selection UI
  const [connectingRepo, setConnectingRepo] = useState(null); // Track connecting repo ID
  const [repoStats, setRepoStats] = useState({});
  const [loadingStats, setLoadingStats] = useState({});
  const [expandedRepos, setExpandedRepos] = useState({});
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [monthlyAnalytics, setMonthlyAnalytics] = useState({});
  const [loadingMonthlyAnalytics, setLoadingMonthlyAnalytics] = useState({});
  const [dashboardData, setDashboardData] = useState(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [showMonthlyView, setShowMonthlyView] = useState(false);

  const arcTableColumns = [{ label: "Contributor name", key: "name" }];

  const isLargeScreen = windowWidth >= 992;
  const getSidebarWidth = () =>
    !sidebarOpen ? "0px" : sidebarCollapsed ? "80px" : "280px";
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const toggleUserDropdown = () => setUserDropdownOpen(!userDropdownOpen);
  const handleSidebarItemClick = (index) => setActiveSidebarItem(index);
  const handleSidebarCollapseChange = (collapsed) =>
    setSidebarCollapsed(collapsed);

  // ---------- API helpers ----------
  const checkIntegrationStatus = async () => {
    try {
      setCheckingIntegration(true);
      const response = await fetch(`${BACKEND_URL}/github/status/?project_id=${projectId}`, {
        credentials: "include",
      });
      const data = await response.json();

      if (data.connected) {
        setIsGithubConnected(true);
        setIntegrationInfo(data.integration_info);
        setLastSyncTime(data.integration_info.last_sync);
        await fetchConnectedRepos();
      } else {
        setIsGithubConnected(false);
        setIntegrationInfo(null);
      }
    } catch (error) {
      console.error("Error checking integration status:", error);
      setGithubError("Failed to check GitHub integration status");
    } finally {
      setCheckingIntegration(false);
    }
  };

  const fetchConnectedRepos = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/github/repos/?project_id=${projectId}&connected_only=1`, {
        credentials: "include",
      });
      const data = await response.json();

      if (data.repositories) {
        setConnectedRepos(data.repositories);
        setIntegrationInfo(data.integration_info);
        // Eager-load stats for connected repos
        data.repositories.slice(0, 3).forEach((repo) => {
          const [owner, repoName] = repo.full_name.split("/");
          fetchRepoStatsFromDB(owner, repoName);
          fetchWeeklySummary(owner, repoName);
        });
        if (data.repositories.length === 0) {
          setShowAvailableRepos(true); // Auto-show selection if none connected
        }
      } else {
        setGithubError(data.error || "No connected repositories found.");
      }
    } catch (error) {
      console.error("Error fetching connected repositories:", error);
      setGithubError("Failed to fetch connected repositories");
    }
  };

  const fetchAvailableRepos = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/github/repos/?project_id=${projectId}`, {
        credentials: "include",
      });
      const data = await response.json();

      if (data.repositories) {
        // Filter out already connected
        const connectedFullNames = connectedRepos.map(r => r.full_name);
        const unconnected = data.repositories.filter(r => !connectedFullNames.includes(r.full_name));
        setAvailableRepos(unconnected);
      } else {
        setGithubError(data.error || "No repositories found.");
      }
    } catch (error) {
      console.error("Error fetching available repositories:", error);
      setGithubError("Failed to fetch available repositories");
    }
  };

  const handleConnectRepo = async (repoFullName) => {
    setConnectingRepo(repoFullName);
    try {
      const response = await fetch(`${BACKEND_URL}/github/projects/connect/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ project_id: projectId, repo_full_name: repoFullName }),
      });
      const data = await response.json();

      if (data.success) {
        // Refresh connected and available
        fetchConnectedRepos();
        fetchAvailableRepos();
        setShowAvailableRepos(false);
      } else {
        setGithubError(data.error || "Failed to connect repository");
      }
    } catch (error) {
      console.error("Error connecting repository:", error);
      setGithubError("Failed to connect repository");
    } finally {
      setConnectingRepo(null);
    }
  };

  const fetchRepoStatsFromDB = async (owner, repo) => {
    const repoKey = `${owner}/${repo}`;
    setLoadingStats((prev) => ({ ...prev, [repoKey]: true }));
    try {
      const response = await fetch(
        `${BACKEND_URL}/github/repos/${owner}/${repo}/stats/?project_id=${projectId}`,
        { credentials: "include" }
      );
      const data = await response.json();
      if (!data.error) {
        setRepoStats((prev) => ({ ...prev, [repoKey]: data }));
      } else {
        console.error("Repo stats error:", data.error);
        setGithubError(`Failed to fetch stats for ${repoKey}: ${data.error}`);
      }
    } catch (error) {
      console.error("Error fetching repo stats:", error);
      setGithubError(`Failed to fetch stats for ${repoKey}`);
    } finally {
      setLoadingStats((prev) => ({ ...prev, [repoKey]: false }));
    }
  };

  const syncRepositoryData = async (repoId = null) => {
    try {
      setIsSyncing((prev) => ({ ...prev, [repoId || "all"]: true }));
      const url = repoId
        ? `${BACKEND_URL}/github/sync/${repoId}/?project_id=${projectId}`
        : `${BACKEND_URL}/github/sync/all/?project_id=${projectId}`;

      const response = await fetch(url, {
        method: "POST",
        credentials: "include",
      });
      const data = await response.json();

      if (data.success) {
        setLastSyncTime(data.last_sync);

        // Refresh connected repositories list
        await fetchConnectedRepos();

        // Reload stats for expanded repos
        const expandedRepoKeys = Object.keys(expandedRepos).filter(key => expandedRepos[key]);

        for (const repoKey of expandedRepoKeys) {
          const [owner, repoName] = repoKey.split("/");
          await fetchRepoStatsFromDB(owner, repoName);
          await fetchWeeklySummary(owner, repoName);
        }

        // If syncing a specific repo, reload it even if not expanded
        if (repoId) {
          const syncedRepo = connectedRepos.find(r => r.id === repoId);
          if (syncedRepo) {
            const [owner, repoName] = syncedRepo.full_name.split("/");
            await fetchRepoStatsFromDB(owner, repoName);
            await fetchWeeklySummary(owner, repoName);
          }
        }

        // Reload dashboard analytics
        await loadDashboardAnalytics();

        setSyncStatus((prev) => ({
          ...prev,
          [repoId || "all"]: { status: "success", message: data.message },
        }));

        setTimeout(() => {
          setSyncStatus((prev) => ({ ...prev, [repoId || "all"]: undefined }));
        }, 5000);
      } else {
        setSyncStatus((prev) => ({
          ...prev,
          [repoId || "all"]: { status: "error", message: data.error },
        }));
      }
    } catch (error) {
      console.error("Sync error:", error);
      setSyncStatus((prev) => ({
        ...prev,
        [repoId || "all"]: {
          status: "error",
          message: `Sync failed: ${error.message}`
        },
      }));
    } finally {
      setIsSyncing((prev) => ({ ...prev, [repoId || "all"]: false }));
    }
  };

  const loadDashboardAnalytics = async () => {
    setLoadingDashboard(true);
    try {
      const response = await fetch(`${BACKEND_URL}/github/repos/?project_id=${projectId}&connected_only=1`, {
        credentials: "include",
      });
      const data = await response.json();
      if (data.repositories) {
        setDashboardData(data);
      } else {
        setGithubError(data.error || "No dashboard data available");
      }
    } catch (error) {
      console.error("Error loading dashboard analytics:", error);
      setGithubError("Failed to load dashboard analytics");
    } finally {
      setLoadingDashboard(false);
    }
  };

  // ---------- Actions ----------
  const handleGithubConnect = () => {
    window.location.href = `${BACKEND_URL}/github/install/?project_id=${projectId}`;
  };

  const handleDisconnectRepo = async (repoId, repoName) => {
    if (
      !window.confirm(
        `Are you sure you want to disconnect "${repoName}" from this project? Your stored data will not be deleted.`
      )
    )
      return;

    try {
      const response = await fetch(
        `${BACKEND_URL}/github/projects/disconnect/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            project_id: projectId,
            repo_id: repoId,
          }),
        }
      );
      const data = await response.json();

      if (data.success) {
        // Refresh the repositories list
        await fetchConnectedRepos();

        // Show success message
        setSyncStatus((prev) => ({
          ...prev,
          [repoId]: {
            status: "success",
            message: `Successfully disconnected ${repoName}`,
          },
        }));

        setTimeout(() => {
          setSyncStatus((prev) => ({ ...prev, [repoId]: undefined }));
        }, 5000);
      } else {
        setGithubError(
          data.error ||
          `Failed to disconnect ${repoName}`
        );
      }
    } catch (error) {
      console.error("Error disconnecting repository:", error);
      setGithubError(`Failed to disconnect repository: ${error.message}`);
    }
  };

  const toggleRepoExpansion = (repoKey) => {
    setExpandedRepos((prev) => ({ ...prev, [repoKey]: !prev[repoKey] }));
  };

  // ---------- UI helpers ----------
  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const StatCard = ({
    icon,
    title,
    value,
    color = "primary",
    subtitle = null,
  }) => (
    <div className="col-md-3 col-sm-6">
      <div className="card p-3 shadow-sm h-100">
        <div className="d-flex align-items-center">
          <div className={`me-3 text-${color}`}>{icon}</div>
          <div className="flex-grow-1">
            <div className="small text-muted">{title}</div>
            <div className={`h5 mb-0 text-${color}`}>{value}</div>
            {subtitle && <small className="text-muted">{subtitle}</small>}
          </div>
        </div>
      </div>
    </div>
  );

  // ---------- Effects ----------
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setSidebarOpen(window.innerWidth >= 992);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    checkIntegrationStatus();
    const params = new URLSearchParams(window.location.search);
    if (params.get("installed")) {
      setTimeout(() => {
        checkIntegrationStatus();
      }, 1000);
    }
  }, []);

  useEffect(() => {
    if (isGithubConnected) loadDashboardAnalytics();
  }, [isGithubConnected]);

  useEffect(() => {
    if (showAvailableRepos && isGithubConnected) {
      fetchAvailableRepos();
    }
  }, [showAvailableRepos, isGithubConnected]);

  if (checkingIntegration) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <LoadingSpinner title="Loading GitHub integration..." />
      </div>
    );
  }

  // ---------- Main UI ----------
  return (
    <div className="d-flex flex-column min-vh-100">
      {/* Header */}
      <div
        style={{
          marginLeft: isLargeScreen ? getSidebarWidth() : "0px",
          transition: "margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          position: "relative",
          zIndex: 1030,
        }}
      >
        <Header
          sidebarOpen={sidebarOpen}
          toggleSidebar={toggleSidebar}
          isCollapsed={sidebarCollapsed}
          userDropdownOpen={userDropdownOpen}
          toggleUserDropdown={toggleUserDropdown}
          pageTitle="Repository insights"
        />
      </div>

      {/* Sidebar */}
      <Sidebar
        sidebarOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
        activeItem={activeSidebarItem}
        onItemClick={handleSidebarItemClick}
        onCollapseChange={handleSidebarCollapseChange}
      />

      {/* Mobile overlay */}
      {sidebarOpen && !isLargeScreen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1040 }}
          onClick={toggleSidebar}
        ></div>
      )}

      {/* Main */}
      <main
        style={{
          marginLeft: isLargeScreen ? getSidebarWidth() : "0px",
          paddingTop: "70px",
          transition: "margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          minHeight: "calc(100vh - 140px)",
        }}
      >
        <div className="container-fluid px-4">
          {/* Page header + controls */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="d-flex flex-column">
                <div className="d-flex justify-content-between align-items-center flex-wrap mb-3 mt-3">
                  <div>
                    <h1 className="display-9 fw-bold text-gradient mb-0">
                      Repository insights
                    </h1>
                    {connectedRepos.length > 0 && (
                      <p className="text-muted mt-2 mb-0">
                        <span>
                          <strong>Username:</strong>{" "}
                          {connectedRepos[0].full_name?.split("/")?.[0] || "—"}
                        </span>
                        <span style={{ marginLeft: "10px" }}>
                          <strong>Repository Name:</strong>{" "}
                          {connectedRepos[0].full_name || "—"}
                        </span>
                      </p>
                    )}
                  </div>

                  {/* Controls cluster */}
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span
                      className={`badge ${isGithubConnected ? "bg-success" : "bg-danger"
                        } me-1`}
                    >
                      {isGithubConnected ? "Connected" : ""}
                    </span>
                    {isGithubConnected &&
                      integrationInfo &&
                      !integrationInfo.token_valid && (
                        <span className="badge bg-warning me-1">
                          Token Expired
                        </span>
                      )}
                    {isGithubConnected && lastSyncTime && (
                      <span className="text-muted small me-2">
                        Last synced: {formatDate(lastSyncTime)}
                      </span>
                    )}

                    {isGithubConnected && (
                      <>
                        <button
                          className="btn btn-sm btn-outline-primary d-flex align-items-center"
                          style={{ background: "rgb(117, 196, 192)", border: "rgb(117, 196, 192)", color: "white", padding: "10px 15px" }}
                          onClick={() => syncRepositoryData()}
                          disabled={isSyncing["all"]}
                          title="Sync all repositories"
                        >
                          {isSyncing["all"] ? (
                            <>
                              <div
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              >

                              </div>

                            </>
                          ) : (
                            <>
                              <FaSync />
                            </>
                          )}
                        </button>



                        {connectedRepos.length > 0 && (
                          <div className="dropdown">
                            <button
                              className="btn btn-sm btn-outline-warning d-flex align-items-center"
                              style={{ background: "rgb(248, 130, 129)", border: "rgb(248, 130, 129)", color: "white", padding: "10px 15px" }}
                              type="button"
                              id="disconnectDropdown"
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                              title="Disconnect a repository"
                            >
                              <FaUnlink />
                            </button>
                            <ul className="dropdown-menu" aria-labelledby="disconnectDropdown">
                              {connectedRepos.map((repo) => (
                                <li key={repo.id}>
                                  <button
                                    className="dropdown-item"
                                    onClick={() => handleDisconnectRepo(repo.id, repo.full_name)}
                                  >
                                    {repo.full_name}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </>
                    )}

                    {/* {!isGithubConnected && (
                      <button
                        className="btn btn-sm btn-primary d-flex align-items-center"
                        onClick={handleGithubConnect}
                      >
                        <FaGithub className="me-2" />
                        Connect GitHub
                      </button>
                    )} */}
                  </div>
                </div>

                {/* Alerts */}
                {githubError && (
                  <div
                    className="alert alert-danger alert-dismissible"
                    role="alert"
                  >
                    {githubError}
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setGithubError(null)}
                    ></button>
                  </div>
                )}

                {syncStatus["all"] && (
                  <div
                    className={`alert alert-${syncStatus["all"].status === "success"
                      ? "success"
                      : "danger"
                      } alert-dismissible`}
                    role="alert"
                  >
                    {syncStatus["all"].message}
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() =>
                        setSyncStatus((prev) => ({ ...prev, all: undefined }))
                      }
                    ></button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Repositories list */}
          <div className="row g-4">
            {isGithubConnected ? (
              <>
                {connectedRepos.length > 0 ? (
                  connectedRepos.map((repo) => {
                    const [owner, repoName] = repo.full_name.split("/");
                    const repoKey = `${owner}/${repoName}`;
                    const stats = repoStats[repoKey];
                    const isLoading = loadingStats[repoKey];
                    const isExpanded = expandedRepos[repoKey];

                    return (
                      <div key={repo.id} className="col-12">
                        {/* Repo-level sync alert */}
                        {syncStatus[repo.id] && (
                          <div
                            className={`alert alert-${syncStatus[repo.id].status === "success"
                              ? "success"
                              : "danger"
                              } alert-dismissible mt-2`}
                            role="alert"
                          >
                            {syncStatus[repo.id].message}
                            <button
                              type="button"
                              className="btn-close"
                              onClick={() =>
                                setSyncStatus((prev) => ({
                                  ...prev,
                                  [repo.id]: undefined,
                                }))
                              }
                            ></button>
                          </div>
                        )}

                        {/* Body */}
                        <div className="mt-3">
                          {stats ? (
                            <>
                              {/* Top metrics */}
                              <div className="row g-3">
                                <div className="col-md-12 col-12 col-sm-12 col-lg-12">
                                  <StatCards
                                    summary={stats}
                                    title="Repository summary"
                                  />
                                </div>
                              </div>

                              <div className="row mb-3">
                                <div className="col-12">
                                  {loadingWeekly[repoKey] ? (
                                    <div className="py-3 d-flex justify-content-center">
                                      <LoadingSpinner title="Loading weekly trend..." />
                                    </div>
                                  ) : (
                                    <WeeklyTrendAreaChart
                                      weekly={weeklySummary[repoKey]}
                                      owner={owner}
                                      project_id={projectId}
                                      repo={repoName}
                                      apiBase={`${BACKEND_URL}/github`}
                                      metrics={[
                                        "commits",
                                        "issues_opened",
                                        "prs_opened",
                                        "prs_merged",
                                        "prs_closed",
                                        "issues_closed",
                                      ]}
                                      height={340}
                                      onDateClick={(iso) => {
                                        console.log(
                                          "Clicked date",
                                          iso,
                                          "for",
                                          repoKey
                                        );
                                      }}
                                    />
                                  )}
                                </div>
                              </div>

                              <div className="row g-3 mb-3 mt-2">
                                <div className="col-lg-6 col-md-6 col-sm-12 col-12">
                                  <HorizontalBarChart stats={stats} />
                                </div>

                                <div className="col-lg-6 col-md-6 col-12 col-sm-12">
                                  <TopTagsChart stats={stats} />
                                </div>
                              </div>

                              {/* Expanded detail */}
                              {isExpanded && (
                                <div className="mt-3">
                                  <div className="alert alert-info py-2">
                                    <small>
                                      <strong>Data:</strong>{" "}
                                      {stats.data_source || "database_and_api"} •
                                      Last updated:{" "}
                                      {stats.last_sync
                                        ? formatDate(stats.last_sync)
                                        : "Never"}
                                    </small>
                                  </div>
                                </div>
                              )}
                            </>
                          ) : isLoading ? (
                            <div className="py-4 d-flex justify-content-center">
                              <LoadingSpinner title="Loading repository statistics..." />
                            </div>
                          ) : (
                            <div className="text-muted py-3">
                              Repository data available in database. Click{" "}
                              <em>Load stats</em> to view analytics.
                              <div className="row mt-2">
                                <div className="col-md-6">
                                  <small>
                                    <strong>Stars:</strong>{" "}
                                    {repo.stars_count || 0}
                                  </small>
                                  <br />
                                  <small>
                                    <strong>Forks:</strong>{" "}
                                    {repo.forks_count || 0}
                                  </small>
                                  <br />
                                  <small>
                                    <strong>Language:</strong>{" "}
                                    {repo.language || "N/A"}
                                  </small>
                                </div>
                                <div className="col-md-6">
                                  <small>
                                    <strong>Open Issues:</strong>{" "}
                                    {repo.open_issues_count || 0}
                                  </small>
                                  <br />
                                  <small>
                                    <strong>Size:</strong> {repo.size || 0} KB
                                  </small>
                                  <br />
                                  <small>
                                    <strong>Updated:</strong>{" "}
                                    {repo.updated_at_github
                                      ? formatDate(repo.updated_at_github)
                                      : "N/A"}
                                  </small>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : !showAvailableRepos && (
                  <div className="col-12 text-center py-4">
                    <FaGitAlt size={48} className="text-muted mb-3" />
                    <h5>No repository connected to this project</h5>
                    <p className="text-muted">
                      Connect a GitHub repository to view stats and activity.
                    </p>
                    <button
                      className="btn btn-outline-primary"
                      onClick={() => setShowAvailableRepos(true)}
                    >
                      <FaPlus className="me-2" />
                      Connect a Repository
                    </button>
                  </div>
                )}

                {/* Available repos selection modal/panel */}
                {showAvailableRepos && (
                  <div className="mt-4">
                    <div className="card shadow-sm">
                      <div className="card-header d-flex justify-content-between align-items-center">
                        <h6 className="mb-0" style={{ paddingTop: "5px", paddingBottom: "5px" }}>Available repositories</h6>
                        {/* <button
                          className="btn btn-sm btn-close"
                          onClick={() => setShowAvailableRepos(false)}
                        /> */}
                      </div>
                      <div className="card-body">
                        {availableRepos.length > 0 ? (
                          availableRepos.map((repo) => (
                            <div
                              key={repo.id}
                              className="d-flex justify-content-between align-items-center mb-3"
                            >
                              <div>
                                <strong>{repo.full_name}</strong>
                                <small className="d-block text-muted">
                                  {repo.description || "No description"}
                                </small>
                              </div>
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={() => handleConnectRepo(repo.full_name)}
                                disabled={connectingRepo === repo.full_name}
                                style={{ background: "rgb(117, 196, 192)", padding: "8px 15px", border: "rgb(117, 196, 192)", color: "white" }}
                              >
                                {connectingRepo === repo.full_name ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status">
                                      <span className="visually-hidden">Connecting...</span>
                                    </span>
                                    Connecting...
                                  </>
                                ) : (
                                  <>
                                    <FaPlus className="me-1" />
                                    Connect
                                  </>
                                )}
                              </button>
                            </div>
                          ))
                        ) : (
                          <p className="text-muted">
                            No additional repositories available. Refresh repositories or add more on GitHub.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "70vh" }}>
                <div className="card shadow-lg text-center p-5 border-0" style={{ maxWidth: "500px", width: "100%" }}>
                  <div className="d-flex flex-column align-items-center">
                    <FaGithub size={72} className="text-muted mb-4" />

                    <h3 className="fw-bold mb-3">Connect your GitHub account</h3>

                    <p className="text-muted mb-4" style={{ lineHeight: "1.6" }}>
                      View repository statistics, track tagged issues, monitor commits,
                      and analyze activity across your projects.
                    </p>

                    <button
                      className="btn btn-lg text-white"
                      onClick={handleGithubConnect}
                      style={{
                        backgroundColor: "rgb(117, 196, 192)",
                        borderColor: "rgb(117, 196, 192)",
                        padding: "12px 32px",
                        borderRadius: "8px",
                        fontWeight: "500",
                        transition: "0.3s ease",
                      }}
                      onMouseOver={(e) =>
                        (e.currentTarget.style.backgroundColor = "rgb(98, 172, 169)")
                      }
                      onMouseOut={(e) =>
                        (e.currentTarget.style.backgroundColor = "rgb(117, 196, 192)")
                      }
                    >
                      <FaGithub className="me-2" size={18} />
                      Connect with GitHub
                    </button>
                  </div>
                </div>
              </div>

            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer sidebarOpen={sidebarOpen} />
    </div>
  );
}

export default GithubStats;